import {produce} from 'immer';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {sliceArray} from '../../../components/Pagination';
import {
  FetchRegistryArtifactCollectionsPartialQueryResult,
  Maybe,
  RegistrySearchResultFragmentFragment,
  useFetchRegistryArtifactCollectionsPartialQuery,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {SingleRegistrySearchContext} from '../common/RegistriesSearchContextProvider';
import {RegistrySearchCollectionType} from '../common/types';
import {
  FetchArtifactCollectionsResult,
  parseRegistrySearchCollections,
} from '../RegistriesHomepage/hooks';

function getSingleRegistryCollectionsSearchResult(
  data: FetchRegistryArtifactCollectionsPartialQueryResult['data']
): Maybe<RegistrySearchResultFragmentFragment> {
  return data?.project?.artifactCollectionsSearch ?? null;
}
type RegistryArtifactCollectionsHookProps = {
  entityName: string;
  projectName: string;
  pageSize: number;
};

export const useSearchablePaginatedSingleRegistryArtifactCollections = ({
  entityName,
  projectName,
  pageSize,
}: RegistryArtifactCollectionsHookProps): FetchArtifactCollectionsResult => {
  // TODO: parameterize this to reduce duplicated code
  const getSearchResult = getSingleRegistryCollectionsSearchResult;

  const {searchFilter} = useContext(SingleRegistrySearchContext);
  const [page, setPage] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<
    RegistrySearchCollectionType[]
  >([]);

  // In the backend, fetching collections without member artifacts is reasonably fast, so we can fetch a larger batch on page load.  However, search could use further optimization.
  // For now, use a smaller batch size when there's a non-empty search term so the user can see results faster.
  const {refetch, fetchMore, data, loading} =
    useFetchRegistryArtifactCollectionsPartialQuery({
      variables: {
        entityName,
        projectName,
        searchTerm: searchFilter,
        limit: searchFilter ? pageSize : 100,
        artifactsLimit: 0,
      },
      onCompleted: data => {
        // No analytics tracking for now, for parity with existing single registry behavior
      },
      context: propagateErrorsContext(),
      // This updates data whenever fetchMore is called and results in
      // duplicate queries being made, so we need to make sure we don't
      // append the same data twice in updateQuery
      notifyOnNetworkStatusChange: true,
    });

  const fetchMoreCollections = useCallback(
    (cursor: string) => {
      fetchMore({
        variables: {
          entityName,
          projectName,
          searchTerm: searchFilter,
          cursor,
          limit: pageSize,
          artifactsLimit: 0,
        },
        updateQuery: (prev, {fetchMoreResult}) => {
          const searchResult = getSearchResult(fetchMoreResult);
          if (!searchResult) {
            return prev;
          }
          return produce(prev, draft => {
            const draftSearchResult = getSearchResult(draft);
            if (
              draftSearchResult &&
              draftSearchResult.pageInfo.endCursor !==
                searchResult.pageInfo?.endCursor
            ) {
              draftSearchResult.pageInfo = searchResult.pageInfo;
              draftSearchResult.edges.push(...searchResult.edges);
            }
          });
        },
      });
    },
    [
      fetchMore,
      entityName,
      projectName,
      searchFilter,
      getSearchResult,
      pageSize,
    ]
  );

  const {edges, pageInfo} = useMemo(() => {
    const searchResult = getSearchResult(data);
    return {
      edges: searchResult?.edges ?? [],
      pageInfo: searchResult?.pageInfo,
    };
  }, [data, getSearchResult]);

  useEffect(() => {
    // check if we need to fetch more data for the current page
    if (edges.length < (page + 1) * pageSize && pageInfo?.hasNextPage) {
      fetchMoreCollections(pageInfo?.endCursor ?? '');
    } else {
      // if we don't need to fetch more, set the current page
      const currentCollections: RegistrySearchCollectionType[] =
        parseRegistrySearchCollections(sliceArray(edges, page, pageSize));
      setCurrentPage(currentCollections);
    }
  }, [edges, pageInfo, page, pageSize, fetchMoreCollections]);

  // reset the view to the first page if the search filter changes
  useEffect(() => {
    setPage(0);
  }, [searchFilter]);

  return {
    currentPage: currentPage,
    loading: loading,
    refetch: refetch,
    totalCollectionsLoaded: edges?.length ?? 0,
    onPageChange: setPage,
    page: page,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
};

// Incremental, one-off fetch of all (single) registry artifact collections without member artifacts
// Needed to maintain behavior of CreateRegisteredCollectionDrawer
export const useAllSingleRegistryArtifactCollectionsWithoutMembers = ({
  entityName,
  projectName,
}: {
  entityName: string;
  projectName: string;
}): {
  collections: RegistrySearchCollectionType[];
  loading: boolean;
  refetch: () => void;
} => {
  // TODO: parameterize this to reduce duplicated code
  const getSearchResult = getSingleRegistryCollectionsSearchResult;
  const [collections, setCollections] = useState<
    RegistrySearchCollectionType[]
  >([]);

  const queryVariables = useMemo(
    () => ({
      entityName,
      projectName,
      searchTerm: '',
      limit: 100,
      artifactsLimit: 0,
    }),
    [entityName, projectName]
  );

  const {refetch, fetchMore, data, loading, error} =
    useFetchRegistryArtifactCollectionsPartialQuery({
      variables: queryVariables,
      context: propagateErrorsContext(),
    });

  const fetchMoreCollections = useCallback(
    (cursor: string) => {
      fetchMore({
        variables: {...queryVariables, cursor},
        updateQuery: (prev, {fetchMoreResult}) => {
          const searchResult = getSearchResult(fetchMoreResult);
          if (!searchResult) {
            return prev;
          }
          return produce(prev, draft => {
            const draftSearchResult = getSearchResult(draft);
            if (draftSearchResult) {
              draftSearchResult.pageInfo = searchResult.pageInfo;
              draftSearchResult.edges.push(...searchResult.edges);
            }
          });
        },
      });
    },
    [fetchMore, getSearchResult, queryVariables]
  );

  const {edges, pageInfo} = useMemo(() => {
    return {
      edges: getSearchResult(data)?.edges ?? [],
      pageInfo: getSearchResult(data)?.pageInfo,
    };
  }, [data, getSearchResult]);

  useEffect(() => {
    // check if we need to fetch more data to gather all collections
    if (pageInfo?.hasNextPage) {
      fetchMoreCollections(pageInfo?.endCursor ?? '');
    } else {
      // if we don't need to fetch more, filter and parse
      const parsedCollections: RegistrySearchCollectionType[] =
        parseRegistrySearchCollections(edges);
      setCollections(parsedCollections);
    }
  }, [edges, pageInfo, fetchMoreCollections]);

  return {collections, loading, refetch};
};
